import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = 'domingao_access_token';
const REFRESH_KEY = 'domingao_refresh_token';

const AuthContext = createContext(null);

export const getAuthToken = () => {
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
};

// Axios interceptor to attach Bearer token automatically on all requests
axios.interceptors.request.use((config) => {
  const t = getAuthToken();
  if (t && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${t}`;
  }
  return config;
});

export const formatApiError = (err) => {
  const d = err?.response?.data?.detail;
  if (!d) return err?.message || 'Erro inesperado';
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(' ');
  return typeof d === 'object' && d.msg ? d.msg : JSON.stringify(d);
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);   // null=unknown/loading, false=guest, obj=logged
  const [loading, setLoading] = useState(true);

  const setSession = (data) => {
    if (data.access_token) localStorage.setItem(TOKEN_KEY, data.access_token);
    if (data.refresh_token) localStorage.setItem(REFRESH_KEY, data.refresh_token);
  };

  const fetchMe = useCallback(async () => {
    const t = getAuthToken();
    if (!t) { setUser(false); setLoading(false); return; }
    try {
      const { data } = await axios.get(`${API}/auth/me`);
      setUser(data);
    } catch {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(REFRESH_KEY);
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMe(); }, [fetchMe]);

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    setSession(data);
    // eslint-disable-next-line no-unused-vars
    const { access_token, refresh_token, ...userData } = data;
    setUser(userData);
    return userData;
  };

  const register = async (name, email, password) => {
    const { data } = await axios.post(`${API}/auth/register`, { name, email, password });
    setSession(data);
    // eslint-disable-next-line no-unused-vars
    const { access_token, refresh_token, ...userData } = data;
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
    setUser(false);
  };

  const refreshMe = fetchMe;

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refreshMe }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth deve ser usado dentro de <AuthProvider>');
  return ctx;
};
